import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import CardDropdown from "components/common/CardDropdown";
import useAdvanceTable from "hooks/useAdvanceTable";
import useClientIDs from "hooks/clientIds/useClientIds";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import { Alert, Dropdown, Spinner, Col, Row } from "react-bootstrap";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { ClientIDDetailsDrawer } from "./ClientIDDetailsDrawer";
import { toast } from "react-toastify";
import useFormattedDate from "hooks/useFormatDate";
import { Input, Empty, Select } from "antd";
import paths from "routes/paths";

const TAB_CONFIG = [
  { key: "ALL", label: "🌍 ALL STATUS" },
  { key: "ACTIVE", label: "\u2705 ACTIVE" },
  { key: "RATE_LIMITED", label: "\u23F1\uFE0F RATE_LIMITED" },
  { key: "THROTTLED", label: "\u{1F422} THROTTLED" },
  { key: "SUSPENDED", label: "\u23F8\uFE0F SUSPENDED" },
  { key: "BLOCKED", label: "\u26D4 BLOCKED" },
];

export const ClientIDs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams();
  const [activeTab, setActiveTab] = useState("ALL");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDrawerInEditMode, setStartDrawerInEditMode] = useState(
    Boolean(location.state?.edit)
  );
  const isSearchRequest = searchTerm.trim().length >= 3;
  const isDrawerOpen = Boolean(clientId);

  const {
    clientIDs,
    loading,
    fetchClientIDs,
    fetchClientStats,
    fetchClientIDById,
    searchClients,
    selectedID,
    updateClientID,
    clientStats,
    error,
    totalPages,
    totalElements,
  } = useClientIDs(page, pageSize, activeTab, searchTerm);

  const handleViewClientID = (id) => {
    setStartDrawerInEditMode(false);
    navigate(paths.clientIdDetails(id));
  };

  useEffect(() => {
    if (searchTerm.length > 0 && searchTerm.length < 3) return;

    const delayDebounceFn = setTimeout(() => {
      const hasStatusFilter = activeTab !== "ALL";
      const hasSearchQuery = searchTerm.trim().length >= 3;

      if (!hasStatusFilter && !hasSearchQuery) {
        fetchClientIDs();
        return;
      }
      searchClients(searchTerm, activeTab, page, pageSize);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, activeTab, page, pageSize, fetchClientIDs, searchClients]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchClientStats(searchTerm);
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [fetchClientStats, searchTerm]);

  useEffect(() => {
    if (!isDrawerOpen || !clientId) return;
    fetchClientIDById(clientId).catch((err) => {
      toast.error(err?.response?.data?.message || "Failed to fetch client ID details", {
        theme: "colored",
      });
      navigate(paths.clientIds, { replace: true });
    });
  }, [isDrawerOpen, clientId, fetchClientIDById, navigate]);

  useEffect(() => {
    if (!isDrawerOpen) {
      setStartDrawerInEditMode(false);
      return;
    }
    setStartDrawerInEditMode(Boolean(location.state?.edit));
  }, [isDrawerOpen, location.state]);

  const handleCloseDrawer = () => {
    setStartDrawerInEditMode(false);
    navigate(paths.clientIds);
  };

  const handleTabChange = (tabKey) => {
    setPage(0);
    setActiveTab(tabKey);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(0);
  };

 const formatKey = (key) => (key && key.length > 18 ? `${key.slice(0, 18)}...` : key);
  const { formatDateTime } = useFormattedDate();

  const getStatusStyle = (status) => {
    const statusStyles = {
      ACTIVE: { bg: "#e8f7e3", text: "#2e7d32", border: "#d5ebd0" },
      NEW: { bg: "#e3f2fd", text: "#1565c0", border: "#bbdefb" },
      BLOCKED: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
      SUSPENDED: { bg: "#ffedd5", text: "#c2410c", border: "#fdba74" },
      INACTIVE: { bg: "#f3f4f6", text: "#6b7280", border: "#d1d5db" },
      RATE_LIMITED: { bg: "#fef9c3", text: "#a16207", border: "#fde68a" },
      THROTTLED: { bg: "#ffedd5", text: "#c2410c", border: "#fdba74" },
      DELETED: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
      DEFAULT: { bg: "#f5f5f5", text: "#616161", border: "#e0e0e0" },
    };
    const style = statusStyles[status] || statusStyles.DEFAULT;
    return {
      padding: "2px 6px",
      borderRadius: "4px",
      fontSize: "0.7rem",
      fontWeight: 600,
      lineHeight: 1.2,
      border: `1px solid ${style.border}`,
      backgroundColor: style.bg,
      color: style.text,
      cursor: "default",
      display: "inline-block",
      whiteSpace: "nowrap",
      verticalAlign: "middle",
    };
  };

const columns = useMemo(() => [
    {
      accessorKey: "id",
      header: "Client ID",
      meta: { headerProps: { className: "text-900 fw-bold" } }, // Consistent Bold Header
      cell: ({ getValue }) => {
        const value = getValue() || "None";
        return (
          <div
            title={value}
            style={{
              maxWidth: 90,        // Matching Users spec
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 13,        // Exact font size from Users
              fontWeight: 400      // Keep content normal weight
            }}
          >
            {formatKey(value)}
          </div>
        );
      },
    },
    {
      accessorKey: "apiKeyId",
      header: "API Key",
      meta: { headerProps: { className: "text-900 fw-bold" } },
      cell: ({ getValue }) => {
        const value = getValue() || "None";
        return (
          <div
            title={value}
            style={{
              maxWidth: 110,       // Slightly wider for keys
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 13,
              fontWeight: 400
            }}
          >
            {formatKey(value)}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const status = String(getValue() || "UNKNOWN").toUpperCase();
        
        // We use the exact style rules from the Users SubtleBadge implementation
        const pillStyle = {
          ...getStatusStyle(status), // Inherit your original colors/borders
          borderRadius: "4px",        // Forced "pill" design
          padding: "2px 6px",         // Compact padding
          fontSize: "0.7rem",
          fontWeight: 600,            // Semi-bold status text
          textTransform: "uppercase" 
        };

        return (
          <div className="text-center">
            <span style={pillStyle}>{status.replaceAll("_", " ")}</span>
          </div>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center fw-bold" },
        cellProps: { className: "text-center" },
      },
    },
    {
      accessorKey: "createdAt",
      header: "Created At",
      meta: { headerProps: { className: "text-900 fw-bold" } },
      cell: ({ getValue }) => (
        <span className="fw-normal" style={{ fontSize: 12,  color: "#6c757d" }}>
          {formatDateTime(getValue())}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      meta: {
        headerProps: { className: "text-900 text-center fw-bold" },
        cellProps: { className: "text-center" },
      },
      cell: ({ row }) => (
        <CardDropdown drop="start">
          <div className="py-2 fw-normal">
            <Dropdown.Item 
              onClick={() => handleViewClientID(row.original.id)} 
              style={{ fontSize: 13 }}
            >
              View/edit
            </Dropdown.Item>
          </div>
        </CardDropdown>
      ),
    },
  ], [handleViewClientID, formatDateTime]);

  const table = useAdvanceTable({
    data: clientIDs,
    columns,
    sortable: true,
    pagination: true,
    selection: true,
    perPage: pageSize,
  });

  const statusStats = [
    { key: "ACTIVE", label: "Active", border: "#16a34a", text: "#16a34a", count: clientStats.ACTIVE || 0 },
    { key: "RATE_LIMITED", label: "Rate Limited", border: "#ca8a04", text: "#ca8a04", count: clientStats.RATE_LIMITED || 0 },
    { key: "THROTTLED", label: "Throttled", border: "#ea580c", text: "#ea580c", count: clientStats.THROTTLED || 0 },
    { key: "SUSPENDED", label: "Suspended", border: "#c2410c", text: "#c2410c", count: clientStats.SUSPENDED || 0 },
    { key: "BLOCKED", label: "Blocked", border: "#dc2626", text: "#dc2626", count: clientStats.BLOCKED || 0 },
  ];

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading style={{ fontSize: 16 }}>
          <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
          Error
        </Alert.Heading>
        <p style={{ fontSize: "13.33px" }}>{error}</p>
      </Alert>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="poll"
        title="Client IDs"
        subtitle="Monitor and manage your client credentials."
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      {/* ================= STATS PANEL (Users Styling) ================= */}
      <div
        className="mb-4 p-3 rounded-3 d-flex flex-wrap gap-2"
        style={{ background: "#f7f5f5", border: "1px solid #e6edf5" }}
      >
        {statusStats.map((item) => (
          <div
            key={item.key}
            className="p-2 rounded-3 d-flex flex-column align-items-center justify-content-center"
            style={{
              minWidth: 100,
              backgroundColor: "#fff",
              borderLeft: `4px solid ${item.border}`,
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 600, color: "#64748b", marginBottom: 2 }}>
              {item.label.toUpperCase()}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: item.text }}>
              {item.count}
            </div>
          </div>
        ))}
      </div>

      <AdvanceTableProvider {...table}>
        {/* ================= SEARCH + FILTERS) ================= */}
        <Row className="mb-2 g-3 align-items-center">
          <Col md={8} lg={9}>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              {/* Search Input - Exact 30px height and 12px radius */}
              <Input
                prefix={<span style={{ fontSize: 14 }}>🔍</span>}
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                style={{
                  borderRadius: 12,
                  height: 30,
                  flex: "1 1 300px",
                  maxWidth: 300,
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center"
                }}
                suffix={
                  loading && isSearchRequest ? (
                    <Spinner animation="border" size="sm" variant="success" style={{ width: 12, height: 12 }} />
                  ) : null
                }
              />

              {/* Filter Select - Exact 28px height */}
              <Select
                value={activeTab}
                onChange={handleTabChange}
                style={{
                  minWidth: 180,
                  height: 28,
                  fontSize: "13px"
                }}
                options={TAB_CONFIG.map(({ key, label }) => ({
                  label: label,
                  value: key,
                }))}
              />
            </div>
          </Col>

          {/* Results Count - Exactly matching the Affiliations text style */}
          <Col md={4} lg={3} className="text-end">
            <div style={{ fontSize: 13, color: "#64748b" }}>
              Results: <span className="fw-semibold text-dark">{clientIDs.length}</span> of <span className="fw-semibold text-dark">{totalElements || 0}</span>
            </div>
          </Col>
        </Row>

        {/* ================= TABLE ================= */}
  <div className="position-relative">
          {loading && (
            <div 
              className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center" 
              style={{ 
                zIndex: 10, 
                backgroundColor: clientIDs.length > 0 ? 'rgba(255, 255, 255, 0.6)' : '#fff',
                minHeight: 300,
                borderRadius: '8px'
              }}
            >
              <div className="text-center">
                <Spinner animation="border" variant="success" size="sm" />
                <div className="mt-2 text-500 fs-10 fw-medium">Updating data...</div>
              </div>
            </div>
          )}

          {clientIDs.length > 0 ? (
            <>
              <div className="mx-n1 px-1">
                <AdvanceTable
                  headerClassName="bg-light text-nowrap align-middle text-dark"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    bordered: true,
                    striped: true,
                    className: "fs-10 mb-0 overflow-hidden",
                  }}
                />
              </div>
              <div className="mt-3">
                <TablePaginationFooter
                  page={page}
                  totalPages={totalPages}
                  totalElements={totalElements}
                  onPrev={page > 0 ? () => setPage(page - 1) : undefined}
                  onNext={page < totalPages - 1 ? () => setPage(page + 1) : undefined}
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  pageSizeOptions={[10, 20, 30, 50, 100]}
                />
              </div>
            </>
          ) : !loading && (
            <div className="py-5 bg-white rounded-3 border border-200 text-center">
              <Empty description={searchTerm.trim() ? `No matching client IDs found` : `No clients found.`} />
            </div>
          )}
        </div>
      </AdvanceTableProvider>

      <ClientIDDetailsDrawer
        selectedID={selectedID}
        loading={loading}
        showDrawer={isDrawerOpen}
        setShowDrawer={(show) => { if (!show) handleCloseDrawer(); }}
        startInEditMode={startDrawerInEditMode}
        updateClientID={updateClientID}
      />
    </>
  );
};
